import { Component, Injector, Input, OnInit } from '@angular/core';
import {
  AbstractComponent,
  AttendanceCopilotInsightService,
  CopilotInsightUiState,
  CopilotInsightView,
  InsightTrend,
} from 'lib-trend-core';
import { Observable } from 'rxjs';
import { isToday } from './insight-time.util';
import { badgeTone, riskKey, trendArrow } from './insight-tones.util';

const KEYS = 'attendance.copilot.insight.';
const SUMMARY_KEYS = 'attendance.copilot.summary.';

/**
 * The insight pane of the Copilot drawer (SPEC-2026-10-copilot-attendant-insight, RF-28 to
 * RF-35; prototype D): its signals, its states and the update button. The content is rendered by
 * `attendance-copilot-insight-content`; the state lives in `AttendanceCopilotInsightService`.
 */
@Component({
  selector: 'attendance-copilot-insight',
  templateUrl: './attendance-copilot-insight.component.html',
  styleUrls: ['./attendance-copilot-insight.component.scss'],
  standalone: false,
})
export class AttendanceCopilotInsightComponent extends AbstractComponent implements OnInit {
  @Input() attendanceClosed = false;

  readonly state$: Observable<CopilotInsightUiState> = this.insightService.select();
  timezone = '';

  constructor(
    injector: Injector,
    private readonly insightService: AttendanceCopilotInsightService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.timezone = this.getTimezoneCompanyCurrentUser();
  }

  closed(view: CopilotInsightView): boolean {
    return this.attendanceClosed || view.readOnly;
  }

  /** A generation in progress; a closed conversation leaves that state (RF-34). */
  generating(state: CopilotInsightUiState): boolean {
    return state.view?.state === 'generating' && !this.closed(state.view);
  }

  /** The button waits for a message after the insight, and for the end of a generation (RF-30). */
  canUpdate(state: CopilotInsightUiState): boolean {
    return state.newMessages > 0 && !this.generating(state) && !state.requesting;
  }

  progressKey(state: CopilotInsightUiState): string {
    return KEYS + (state.view?.insight ? 'updating' : 'generating');
  }

  generatedKey(generatedAt: string): string {
    return KEYS + (isToday(generatedAt, this.timezone) ? 'generatedToday' : 'generatedOn');
  }

  /** The footer repeats the moment in its short form, beside the update button (prototype D). */
  footerKey(generatedAt: string): string {
    return KEYS + (isToday(generatedAt, this.timezone) ? 'footerToday' : 'footerOn');
  }

  /** The short form of the summary strip, so the footer stays on one line beside the button. */
  newMessagesKey(count: number): string {
    return SUMMARY_KEYS + (count === 1 ? 'newMessagesOne' : 'newMessagesMany');
  }

  /** What the pane states before the first insight (RF-28). */
  waitingKey(view: CopilotInsightView): string {
    const waiting = view.waiting;
    if (!waiting || waiting.messageCount < waiting.n) return KEYS + 'waitingForMessages';
    return KEYS + (waiting.assigned ? 'waitingForNextMessage' : 'waitingForAttendant');
  }

  tone(value: string): string {
    return badgeTone(value);
  }

  arrow(trend: InsightTrend): string {
    return trendArrow(trend);
  }

  riskKey(view: CopilotInsightView): string {
    return riskKey(SUMMARY_KEYS, view.insight?.content.relationship.churnRisk.level ?? 'low');
  }

  generate(): void {
    this.insightService.generate();
  }
}
